import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';

import { RealPropertyService } from '@modules/real-property-details';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { SettingsService } from '@app/settings.service';
import { Meta } from '@angular/platform-browser';
import { MessageService } from 'primeng/api';
import { SeoService } from '@shared/services/meta-tags.service';
import { NgxMaskModule } from 'ngx-mask';
import { ActivatedRoute } from '@angular/router';
import { Clipboard } from '@angular/cdk/clipboard';
import { ButtonComponent } from '@app/shared/button/button.component';

import { AvatarComponent } from '@shared/components';
import { GalleriaComponent } from '@app/shared/galleria/galleria.component';
import * as models from '@modules/real-property-details/models';
import { CardObjectComponent } from '@app/home/card-object/card-object.component';
import { CardAgentComponent } from '@app/shared/card-agent/card-agent.component';
import { MapComponent } from '@app/shared/map/map.component';
import * as mortgageModels from '@modules/mortgage/models';
import { ObjectListComponent } from '@app/shell/object-list/object-list.component';
import { SellApplication } from '@modules/electronic-signing/models';
import { CreateContractComponent } from '@app/shell/real-property-detail/create-contract/create-contract.component';
import { PanelComponent } from '@app/shared/panel/panel.component';
import { MortgageComponent } from '@app/shell/real-property-detail/mortgage/mortgage.component';
import { SignUpShowingComponent } from '@app/shell/real-property-detail/sign-up-showing/sign-up-showing.component';
import { FavouritesManagementService } from '@modules/real-property-toolbar/services';
import { CalculatorComponent } from '@app/shared/calculator/calculator.component';
import { UtilService } from '@helpers/util.service';
import { ToastModule } from 'primeng/toast';
import {ApplyApplicationComponent} from "@app/shell/apply-application/apply-application.component";

declare const window: any;

enum OperationType {
  '001001' = 'Покупка',
  '001002' = 'Продажа',
  '001003' = 'Аренда',
}

enum OperationTypeDeclension {
  '001001' = 'покупке',
  '001002' = 'продаже',
  '001003' = 'аренде',
}

enum ObjectType {
  '003001' = '-комнатной квартиры',
  '003002' = '-комнатного дома',
  '003003' = '-комнатного коммерческого помещения',
}

enum ObjectTypeHeaderTitle {
  '003001' = '-комнатная квартира',
  '003002' = '-комнатный дом',
  '003003' = '-комнатное коммерческое помещение',
}

@Component({
  selector: 'app-real-property-detail',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonComponent,
    AvatarComponent,
    GalleriaComponent,
    NgxMaskModule,
    CardObjectComponent,
    MapComponent,
    ObjectListComponent,
    CardAgentComponent,
    PanelComponent,
    CreateContractComponent,
    MortgageComponent,
    SignUpShowingComponent,
    CalculatorComponent,
    ToastModule,
    ApplyApplicationComponent,
  ],
  templateUrl: './real-property-detail.component.html',
  styleUrls: ['./real-property-detail.component.scss'],
})
export class RealPropertyDetailComponent implements OnInit {
  application$: Observable<models.SellApplication>;

  currentLanguageAsKey = this._settingsService.getCurrentLanguageAsKey();

  sameRealPropertyList: any[] = [];

  sameObjTotal: number;

  openContractPanel: boolean;
  openMortgageModal: boolean;
  openShowingModal: boolean;

  moreDescriptionText: boolean;

  nearButtons: { id: number; label: string; icon: string, action: string }[] = [
    { id: 1, label: 'Школа', icon: './assets/images/new-design-icons/School (white).svg', action: 'schoolList' },
    { id: 2, label: 'Больница', icon: './assets/images/new-design-icons/Building (white).svg', action: 'hospitalList' },
    { id: 3, label: 'Детский сад', icon: './assets/images/new-design-icons/Kindergarten (white).svg', action: 'kindergartenList' },
    { id: 4, label: 'Трц', icon: './assets/images/new-design-icons/House (white).svg', action: 'shoppingCenterList' },
  ];
  selectedButtonId: number;
  selectedCreditProgram: any;
  objectPrice: number;

  showMoreInfo: boolean;
  isLiked: boolean;

  isTablet$: Observable<boolean>;
  isMobile$: Observable<boolean>;

  creditProgram: mortgageModels.CreditProgramList;
  filtersSameObjects: any;
  calculatorForm: FormGroup;
  @ViewChild('objectList') objectList: ElementRef;
  constructor(
    private _realPropertyService: RealPropertyService,
    private _settingsService: SettingsService,
    private _metaService: Meta,
    private _messageService: MessageService,
    private _seoService: SeoService,
    private _route: ActivatedRoute,
    private clipboard: Clipboard,
    private _favoritesService: FavouritesManagementService,
    private _utilService: UtilService,
  ) {}

  ngOnInit() {
    this.isTablet$ = this._settingsService.isTablet();
    this.isMobile$ = this._settingsService.isMobile();
    this.application$ = this._route.paramMap.pipe(
      filter((params) => params.has('id')),
      map((params) => parseInt(params.get('id'), 10)),
      switchMap((id) => this.#loadData(id)),
    );
  }

  createFiltersForSameObjects(app: models.SellApplication) {
    this.filtersSameObjects = {
      pageNumber: 0,
      pageSize: 8,
      sourceApplicationId: app.id,
      objectPrice: app.sellDataDto?.objectPrice,
      numberOfRooms: app.realPropertyDto?.numberOfRooms,
      objectTypeId: app.objectType?.id,
      districtCode: app.realPropertyDto?.addressDto?.districtCode,
      expired: false,
    };
  }

  #loadData(applicationId: number) {
    return this._realPropertyService.getRealPropertyById(applicationId).pipe(
      tap((app) => {
        this.createFiltersForSameObjects(app);
        this.getSameRealProperty(app, false);
      }),
      tap(({ isFavorite, realPropertyDto, operationType, id, sellDataDto, objectType }) => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        this.isLiked = isFavorite;
        this.objectPrice = sellDataDto.objectPrice;
        const typesOfOperation = OperationType[operationType.code];
        const typesOfObject = ObjectType[objectType.code];
        const titleAddress = [
          realPropertyDto.addressDto?.city?.[this.currentLanguageAsKey],
          realPropertyDto.addressDto?.district?.[this.currentLanguageAsKey] ? 'р-н.' + realPropertyDto.addressDto?.district?.[this.currentLanguageAsKey] : '',
          realPropertyDto.addressDto?.street?.[this.currentLanguageAsKey] ? 'ул.' + realPropertyDto.addressDto?.street?.[this.currentLanguageAsKey] : '',
        ];
        const titlePreview = `${typesOfOperation} ${realPropertyDto.numberOfRooms}${typesOfObject} - №${id}: ${titleAddress.filter((item) => item.length).join(', ')} - за ${
          sellDataDto.objectPrice
        } тенге`;
        const description = `Обявление о ${OperationTypeDeclension[operationType.code]} ${realPropertyDto.numberOfRooms}${typesOfObject} за ${sellDataDto.objectPrice} тенге в ${realPropertyDto.addressDto?.city?.[this.currentLanguageAsKey]} ⭐Недвижимость Казахстана на ivitrina.kz`;

        this._metaService.updateTag({
          property: 'og:type',
          content: 'Website',
        });

        this._seoService.setUrl(location.href);
        this._seoService.setTitle(titlePreview);
        this._seoService.setDescription(description);
      }),
      catchError(() => {
        this._messageService.add({ severity: 'error', summary: 'Не удалось открыть детализацию объекта недвижимости', life: 10000 });
        return of(null);
      }),
    );
  }

  toggleFavorite(appId: number) {
    const favoriteAction = this.isLiked
        ? this._favoritesService.removeRealPropertyObjectFromFavouritesList(appId)
        : this._favoritesService.addRealPropertyObjectToFavourites(appId);
    favoriteAction.subscribe((res: any) => {
      this.isLiked = !this.isLiked;
      if (typeof res !== 'number') {
        this._messageService.add({ severity: 'error', summary: 'Ошибка', detail: res });
      }
    });
  }

  getPropertyTitle(app: models.SellApplication): string {
    const typesOfObject = ObjectTypeHeaderTitle[app.objectType.code];
    return `<span>${app.realPropertyDto.numberOfRooms}${typesOfObject}, ${app.realPropertyDto.totalArea} м<sup><small>2</small></sup>, ${app.realPropertyDto.floor || ''} ${app.realPropertyDto.numberOfFloors ? ' / ' + app.realPropertyDto.numberOfFloors : ''}, ${app.realPropertyDto.addressDto?.street?.[this.currentLanguageAsKey] ? app.realPropertyDto.addressDto?.street?.[this.currentLanguageAsKey] : ''}</span>`;
  }

  getPropertyMediaList(app: models.SellApplication) {
    return [...app.realPropertyDto.virtualTourImageIdList, ...app.realPropertyDto.photoIdList];
  }

  getSameRealProperty(app: models.SellApplication, more?: boolean) {
    if (more) {
      this.filtersSameObjects.pageNumber = this.filtersSameObjects.pageNumber + 1;
      delete this.filtersSameObjects.pageSize;
    }
    this._realPropertyService.getSameRealPropertyList(this.filtersSameObjects).subscribe((res) => {
      const transformedData = res.data.data.map((item) => this._utilService.transItemForCardObject(item, app.agentDto));
      if (!more) {
        this.sameObjTotal = res.total;
        this.sameRealPropertyList = transformedData;
      } else {
        this.sameRealPropertyList.push(...transformedData);
      }
    });
  }

  selectNear(id: number) {
    this.selectedButtonId = id;
  }

  coordinatesOfObject(app: models.SellApplication): string[] {
    const { latitude, longitude } = app.realPropertyDto.addressDto;
    return [longitude, latitude];
  }

  public getObjectDetails(application: SellApplication, field?: string) {
    if (application.realPropertyDto.residentialComplexDto) {
      return application.realPropertyDto.residentialComplexDto.hasOwnProperty(field) && application.realPropertyDto.residentialComplexDto[field] !== null
        ? application.realPropertyDto.residentialComplexDto[field]
        : application.realPropertyDto.generalCharacteristicsDto[field];
    }
    return application.realPropertyDto.generalCharacteristicsDto.hasOwnProperty(field) ? application.realPropertyDto.generalCharacteristicsDto[field] : null;
  }

  getRealPropertyInfo(app: models.SellApplication) {
    const data = [
      {
        label: 'Адрес',
        value: `${app.realPropertyDto.addressDto?.city[this.currentLanguageAsKey]} , p-н ${app.realPropertyDto.addressDto?.district[this.currentLanguageAsKey]} , ${app.realPropertyDto.addressDto?.street[this.currentLanguageAsKey]}`,
      },
      { label: 'Количество комнат', value: app.realPropertyDto.numberOfRooms },
      { label: 'Общая площадь', value: app.realPropertyDto.totalArea },
      { label: 'Этаж', value: app.realPropertyDto.floor },
      { label: 'Состояние', value: app.objectType.name[this.currentLanguageAsKey] },
      { label: 'Жилой комплекс', value: app.realPropertyDto.residentialComplexDto ? app.realPropertyDto.residentialComplexDto.houseName : '' },
      { label: 'Санузел', value: app.realPropertyDto.separateBathroom === null ? '' : app.realPropertyDto.separateBathroom ? 'Да' : 'Нет' },
      { label: 'Материал постройки', value: app.realPropertyDto.residentialComplexDto ? app.realPropertyDto.residentialComplexDto.materialOfConstruction : '' },
      { label: 'Потолки', value: app.realPropertyDto.ceilingHeight },
      { label: 'Лифт', value: this.getObjectDetails(app, 'typeOfElevatorNameList') },
      { label: 'Двор', value: this.getObjectDetails(app, 'yardType') },
      { label: 'Детская площадка', value: this.getObjectDetails(app, 'playground') },
      { label: 'Консьерж', value: this.getObjectDetails(app, 'concierge') },
      { label: 'Планировка', value: app.realPropertyDto.atelier === null ? null : app.realPropertyDto.atelier ? 'Студия' : 'Изолированные комнаты' },
      { label: 'Застройщик', value: !app.realPropertyDto.residentialComplexDto ? null : (app.realPropertyDto.residentialComplexDto.propertyDeveloper ? app.realPropertyDto.residentialComplexDto.propertyDeveloper[this.currentLanguageAsKey] : null) },
      { label: 'Обременение', value: app.sellDataDto.encumbrance ? 'Да' : 'Нет' },
      { label: 'Торг', value: app.sellDataDto.probabilityOfBidding === null ? null : app.sellDataDto.probabilityOfBidding ? 'Да' : 'Нет' },
    ];

    return data.filter((d) => d.value);
  }

  getComplexNear(app: SellApplication) {
    const action = this.nearButtons.find((i) => i.id === this.selectedButtonId).action;
    if (app.realPropertyDto.residentialComplexDto && app.realPropertyDto.residentialComplexDto[action]) {
      const item = app.realPropertyDto.residentialComplexDto[action];
      if (item) {
        return item.map((i: any) => ({ ...i, name: i.name[this.currentLanguageAsKey], streetName: i.street.name[this.currentLanguageAsKey] }))
      }
    }
    return [];
  }

  copyApplicationId(applicationId: number): void {
    if (this.clipboard.copy(applicationId.toString())) {
      this._messageService.add({ severity: 'info', detail: 'Скопировано' });
    }
  }

  shareHandler(appId: number) {
    window.navigator.share({ url: `/real-property/${appId}` });
  }

  navigateToObjects() {
    this.objectList.nativeElement.scrollIntoView({ behavior: 'smooth' });
  }

  prevPage() {
    window.history.back();
  }
}
